import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Firestore, collection, doc, getDocs, limit, onSnapshot, query, where } from '@angular/fire/firestore';
import { filter } from 'rxjs/operators';

import { MODEL_APP_USERS, MODEL_QUEUES, MODEL_TENANTS } from '@aveespa/common/constants/models-metadata.constants';
import { Tenant } from '@aveespa/common/models/tenant/tenant.interface';
import { Department } from '@aveespa/common/models/staff/department.interface';
import { Group } from '@aveespa/common/models/tenant/group.interface';
import { Queue } from '@aveespa/common/models/staff/queue.interface';

import { TenantContextService } from './tenant-context.service';

/**
 * Single source of truth for the current tenant's Firestore configuration.
 *
 * Reads the `tenants/{tenantId}` document in real-time via Firestore's
 * `onSnapshot` listener and exposes the data as Angular signals so any
 * component can react to configuration changes without an explicit reload.
 *
 * Usage:
 *   private readonly _settings = inject(TenantSettingsService);
 *   readonly departments = this._settings.departments;   // computed Signal<Department[]>
 *   readonly hasDepartments = this._settings.hasDepartments; // computed Signal<boolean>
 *
 * This service replaces the removed CompanyConfigService. All configuration
 * previously loaded from a static file now comes from Firestore.
 */
@Injectable({ providedIn: 'root' })
export class TenantSettingsService {

    private readonly _firestore      = inject(Firestore);
    private readonly _tenantContext  = inject(TenantContextService);
    private readonly _destroyRef     = inject(DestroyRef);

    // ── Raw tenant document snapshot ─────────────────────────────────────────
    // Typed as the full Tenant (not just TenantConfig) — system_admin already
    // reads this whole document under Firestore rules (their own tenant),
    // which includes operational fields like smsConfig, not just TenantConfig.
    readonly tenantConfig = signal<Tenant | null>(null);
    /** True once the first snapshot has been received (even if the doc is empty). */
    readonly isLoaded     = signal(false);

    // ── Derived signals ───────────────────────────────────────────────────────

    /** Active (non-archived, non-deleted) departments, sorted by sortOrder then name. */
    readonly departments = computed<Department[]>(() =>
        (this.tenantConfig()?.departments ?? [])
            .filter(d => d.recordStatus !== 'deleted' && d.recordStatus !== 'archived')
            .sort((a, b) =>
                ((a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)) ||
                a.name.localeCompare(b.name),
            ),
    );

    /** All departments including archived (for the settings management view). */
    readonly allDepartments = computed<Department[]>(() =>
        this.tenantConfig()?.departments ?? [],
    );

    readonly hasDepartments  = computed(() => this.departments().length > 0);
    /** True if at least one active user exists in this tenant (one-shot check on load). */
    readonly hasActiveUsers  = signal(false);
    readonly templateGroups  = computed(() => this.tenantConfig()?.templateGroups ?? []);
    readonly companyName     = computed(() => this.tenantConfig()?.companyName ?? '');
    readonly addresses       = computed(() => this.tenantConfig()?.addresses ?? []);
    readonly branding        = computed(() => this.tenantConfig()?.branding ?? null);
    readonly contactPhones   = computed(() => this.tenantConfig()?.contactPhones ?? []);
    readonly contactEmails   = computed(() => this.tenantConfig()?.contactEmails ?? []);
    readonly contactFaxes    = computed(() => this.tenantConfig()?.contactFaxes  ?? []);
    readonly timezone        = computed(() => this.tenantConfig()?.timezone ?? null);
    readonly website         = computed(() => this.tenantConfig()?.website ?? null);
    readonly maxUsers        = computed(() => this.tenantConfig()?.maxUsers ?? null);
    /** Whether this tenant has SMS turned on (TenantSmsConfig.enabled) — gates the SMS delivery-channel option (UX-G-14). */
    readonly smsEnabled      = computed(() => this.tenantConfig()?.smsConfig?.enabled ?? false);

    /** Active (non-archived, non-deleted) access groups, sorted by name. */
    readonly groups = computed<Group[]>(() =>
        (this.tenantConfig()?.groups ?? [])
            .filter(g => g.recordStatus !== 'deleted' && g.recordStatus !== 'archived')
            .sort((a, b) => a.name.localeCompare(b.name)),
    );

    /** All groups including archived (for the settings management view). */
    readonly allGroups = computed<Group[]>(() =>
        this.tenantConfig()?.groups ?? [],
    );

    // ── Queues (separate collection: queues/{queueId}) ────────────────────────
    private readonly _queues = signal<Queue[]>([]);

    /** Active queues for this tenant, sorted by name. */
    readonly queues   = computed<Queue[]>(() =>
        [...this._queues()].sort((a, b) => a.name.localeCompare(b.name)),
    );
    readonly hasQueues = computed(() => this._queues().length > 0);

    // ── Firestore listener lifecycle ──────────────────────────────────────────
    private _unsubscribeSnapshot?: () => void;
    private _unsubscribeQueues?: () => void;

    constructor() {
        // Switch to a new Firestore listener whenever the tenantId changes.
        // takeUntilDestroyed ensures cleanup when the service is destroyed.
        this._tenantContext.tenantId$.pipe(
            takeUntilDestroyed(this._destroyRef),
            filter(Boolean),
        ).subscribe(tenantId => {
            this._stopListening();
            this._startListening(tenantId);
            this._startListeningQueues(tenantId);
        });
    }

    private _startListening(tenantId: string): void {
        const ref = doc(this._firestore, MODEL_TENANTS, tenantId);
        this._unsubscribeSnapshot = onSnapshot(
            ref,
            (snap) => {
                this.tenantConfig.set(snap.exists() ? (snap.data() as Tenant) : null);
                this.isLoaded.set(true);
            },
            () => {
                // Snapshot error — mark as loaded so the UI doesn't spin forever.
                this.isLoaded.set(true);
            },
        );

        getDocs(query(
            collection(this._firestore, MODEL_APP_USERS),
            where('tenantId', '==', tenantId),
            where('recordStatus', '==', 'active'),
            limit(1),
        )).then(snap => this.hasActiveUsers.set(!snap.empty)).catch(() => {});
    }

    private _startListeningQueues(tenantId: string): void {
        this._unsubscribeQueues?.();
        const q = query(
            collection(this._firestore, MODEL_QUEUES),
            where('tenantId', '==', tenantId),
            where('recordStatus', '==', 'active'),
        );
        this._unsubscribeQueues = onSnapshot(
            q,
            (snap) => {
                this._queues.set(snap.docs.map(d => d.data() as Queue));
            },
            () => {},
        );
    }

    private _stopListening(): void {
        this._unsubscribeSnapshot?.();
        this._unsubscribeSnapshot = undefined;
        this._unsubscribeQueues?.();
        this._unsubscribeQueues = undefined;
    }
}
